import React, { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Plus,
  Edit,
  Trash2,
  Eye,
  Search,
  Filter,
  Mail,
  FileText,
  Copy,
  Code,
} from "lucide-react";
import { LoadingCard } from "../components/LoadingSpinner";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "../hooks/use-toast";
import { emailTemplateService } from "../services/emailTemplateService";

// Email template categories
const EMAIL_CATEGORIES = [
  { value: "lead", label: "Leads" },
  { value: "project", label: "Projects" }, 
  { value: "customer", label: "Customers" },
  { value: "event", label: "Events" }
];

export default function EmailTemplates() {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isPreviewDialogOpen, setIsPreviewDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { id, name }

  // New template form state
  const [newTemplate, setNewTemplate] = useState({
    name: "",
    subject: "",
    body: "",
    description: "",
    category: "customer",
    is_html: false
  });

  // Preview variables state
  const [previewVariables, setPreviewVariables] = useState({});

  // All available template variables organized by category
  // IMPORTANT: These must match exact field names in the database models
  const CUSTOMER_VARIABLES = [
    { name: "name", description: "Customer's full name (first name)" },
    { name: "last_name", description: "Customer's last name" },
    { name: "email", description: "Customer's email address" },
    { name: "phone", description: "Customer's phone number" },
    { name: "address", description: "Customer's address" },
    { name: "city", description: "Customer's city" },
    { name: "zip_code", description: "Customer's ZIP code" },
    { name: "status", description: "Customer status (prospect, active, inactive)" },
    { name: "referral_source", description: "How they found you (Google, Yelp, etc.)" },
    { name: "company_account", description: "Company/Business name" },
    { name: "description", description: "Customer description/notes" },
    { name: "spouse", description: "Spouse name" },
    { name: "spouse_email", description: "Spouse email" },
    { name: "spouse_phone", description: "Spouse phone" },
    { name: "hoa", description: "HOA (true/false)" },
    { name: "gate_code", description: "Gate code for property access" },
    { name: "first_email_date", description: "Date of first email contact" },
    { name: "first_call_date", description: "Date of first call contact" },
    { name: "appointment_date", description: "Scheduled appointment date" },
    { name: "budget", description: "Customer's budget" },
    { name: "total_spent", description: "Total amount spent by customer" },
    { name: "projects_count", description: "Number of projects" },
    { name: "join_date", description: "Date customer was added" },
    { name: "created_at", description: "Date customer record was created" },
    { name: "updated_at", description: "Date customer record was last updated" },
  ];

  const PROJECT_VARIABLES = [
    // Project fields
    { name: "name", description: "Project name/title" },
    { name: "jobsite_address", description: "Project jobsite address" },
    { name: "city", description: "Project city" },
    { name: "zip", description: "Project ZIP code" },
    { name: "contract_price", description: "Project contract price" },
    { name: "total_job_cost", description: "Total job cost (with change orders)" },
    { name: "status", description: "Current project status" },
    { name: "priority", description: "Project priority (low, medium, high)" },
    { name: "project_type", description: "Type of project (design_only, construction)" },
    { name: "description", description: "Project description" },
    { name: "start_date", description: "Project start date" },
    { name: "end_date", description: "Estimated completion date" },
    { name: "progress", description: "Project progress percentage" },
    { name: "estimated_construction_budget", description: "Estimated construction budget (design only)" },
    { name: "budget", description: "Project budget (legacy field)" },
    { name: "spent", description: "Amount spent on project" },

    // Payment schedule fields
    { name: "deposit_amount", description: "Deposit (1st payment) amount" },
    { name: "deposit_due_date", description: "Deposit (1st payment) due date" },
    { name: "deposit_description", description: "Deposit (1st payment) name/label" },
    { name: "final_payment_amount", description: "Final payment amount" },
    { name: "final_payment_due_date", description: "Final payment due date" },
    { name: "total_contract", description: "Total contract value (with change orders)" },
    { name: "total_paid", description: "Total amount paid so far" },
    { name: "remaining_balance", description: "Remaining balance owed" },
    
    // Customer fields (for customer linked to project)
    { name: "customer_name", description: "Customer's full name (first name)" },
    { name: "customer_last_name", description: "Customer's last name" },
    { name: "customer_email", description: "Customer's email address" },
    { name: "customer_phone", description: "Customer's phone number" },
    { name: "customer_address", description: "Customer's address" },
    { name: "customer_city", description: "Customer's city" },
    { name: "customer_zip_code", description: "Customer's ZIP code" },
    { name: "customer_status", description: "Customer status (prospect, active, inactive)" },
    { name: "customer_referral_source", description: "How customer found you" },
    { name: "customer_company_account", description: "Customer's company/business name" },
    { name: "customer_description", description: "Customer description/notes" },
    { name: "customer_spouse", description: "Customer's spouse name" },
    { name: "customer_spouse_email", description: "Customer's spouse email" },
    { name: "customer_spouse_phone", description: "Customer's spouse phone" },
    { name: "customer_hoa", description: "Customer's HOA (true/false)" },
    { name: "customer_gate_code", description: "Customer's gate code" },
    { name: "customer_first_email_date", description: "Customer's first email date" },
    { name: "customer_first_call_date", description: "Customer's first call date" },
    { name: "customer_appointment_date", description: "Customer's appointment date" },
    { name: "customer_budget", description: "Customer's budget" },
  ];

  const LEAD_VARIABLES = [
    { name: "name", description: "Lead's full name (first name)" },
    { name: "last_name", description: "Lead's last name" },
    { name: "email", description: "Lead's email address" },
    { name: "phone", description: "Lead's phone number" },
    { name: "address", description: "Lead's street address" },
    { name: "city", description: "Lead's city" },
    { name: "zip_code", description: "Lead's ZIP code" },
    { name: "service_type", description: "Service they're interested in" },
    { name: "project_value", description: "Lead's estimated project value" },
    { name: "budget", description: "Lead's budget" },
    { name: "status", description: "Lead status (new, contacted, qualified, etc.)" },
    { name: "priority", description: "Lead priority (high, medium, low)" },
    { name: "source", description: "Original lead source (backward compatibility)" },
    { name: "referral_source", description: "Where the lead came from (Yelp, Google, etc.)" },
    { name: "notes", description: "Notes about the lead" },
    { name: "assigned_to", description: "Who the lead is assigned to" },
    { name: "company_account", description: "Company account name" },
    { name: "description", description: "Lead description" },
    { name: "spouse", description: "Spouse's name" },
    { name: "spouse_phone", description: "Spouse's phone number" },
    { name: "spouse_email", description: "Spouse's email address" },
    { name: "hoa", description: "HOA (true/false)" },
    { name: "gate_code", description: "Gate code for property access" },
    { name: "first_email_date", description: "Date of first email contact" },
    { name: "first_call_date", description: "Date of first call contact" },
    { name: "appointment_date", description: "Scheduled appointment date" },
    { name: "last_contact", description: "Date of last contact" },
    { name: "created_at", description: "Date lead was created" },
    { name: "updated_at", description: "Date lead was last updated" },
  ];

  const EVENT_VARIABLES = [
    { name: "title", description: "Event/appointment title" },
    { name: "date", description: "Event date" },
    { name: "time", description: "Event time" },
    { name: "location", description: "Event location" },
    { name: "description", description: "Event description" },
  ];

  const COMPANY_VARIABLES = [
    { name: "company_name", description: "Your company name" },
    { name: "company_email", description: "Your business email" },
    { name: "company_phone", description: "Your business phone" },
    { name: "company_address", description: "Your business address" },
    { name: "company_website", description: "Your website" },
    { name: "date", description: "Current date (e.g., October 3, 2025)" },
    { name: "time", description: "Current time (e.g., 3:45 PM)" },
    { name: "year", description: "Current year" },
    { name: "month", description: "Current month" },
  ];

  // Get contextual variables based on template category
  const getAvailableVariables = (category) => {
    let categoryVariables = [];
    
    switch (category) {
      case 'customer':
        categoryVariables = [...CUSTOMER_VARIABLES];
        break;
      case 'project':
        categoryVariables = [...PROJECT_VARIABLES];
        break;
      case 'lead':
        categoryVariables = [...LEAD_VARIABLES];
        break;
      case 'event':
        categoryVariables = [...EVENT_VARIABLES];
        break;
    }
    
    // Always add company variables to all categories
    return [...categoryVariables, ...COMPANY_VARIABLES];
  };

  const queryClient = useQueryClient();
  const { toast } = useToast();

  // API hooks
  const { data: templatesData, isLoading: loading, error } = useQuery({
    queryKey: ['emailTemplates', searchTerm, selectedCategory],
    queryFn: () => emailTemplateService.getAll({
      search: searchTerm,
      category: selectedCategory !== "all" ? selectedCategory : undefined,
      active_only: true
    })
  });

  const createTemplateMutation = useMutation({
    mutationFn: (data) => emailTemplateService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['emailTemplates'] });
      queryClient.invalidateQueries({ queryKey: ['email-templates'] }); // Also invalidate dropdown queries
      toast({ description: "Email template created successfully!" });
      setNewTemplate({
        name: "",
        subject: "",
        body: "",
        description: "",
        category: "customer"
      });
      setIsDialogOpen(false);
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        description: error?.detail || "Failed to create template"
      });
    }
  });

  const updateTemplateMutation = useMutation({
    mutationFn: ({ id, data }) => emailTemplateService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['emailTemplates'] });
      queryClient.invalidateQueries({ queryKey: ['email-templates'] }); // Also invalidate dropdown queries
      toast({ description: "Email template updated successfully!" });
      setEditingTemplate(null);
      setIsEditDialogOpen(false);
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        description: error?.detail || "Failed to update template"
      });
    }
  });

  const deleteTemplateMutation = useMutation({
    mutationFn: (templateId) => emailTemplateService.delete(templateId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['emailTemplates'] });
      queryClient.invalidateQueries({ queryKey: ['email-templates'] }); // Also invalidate dropdown queries
      toast({ description: "Email template deleted successfully!" });
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        description: error?.detail || "Failed to delete template"
      });
    }
  });

  const previewTemplateMutation = useMutation({
    mutationFn: ({ id, variables }) => emailTemplateService.preview(id, variables),
    onSuccess: (data) => {
      setPreviewData(data.data);
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        description: error?.detail || "Failed to preview template"
      });
    }
  });

  const templates = templatesData?.data?.templates || [];

  const handleAddTemplate = async () => {
    createTemplateMutation.mutate(newTemplate);
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate({
      ...template,
      category: template.category || "customer",
      is_html: template.is_html || false
    });
    setIsEditDialogOpen(true);
  };

  const handleUpdateTemplate = async () => {
    updateTemplateMutation.mutate({
      id: editingTemplate.id || editingTemplate._id,
      data: {
        name: editingTemplate.name,
        subject: editingTemplate.subject,
        body: editingTemplate.body,
        description: editingTemplate.description,
        category: editingTemplate.category,
        is_html: editingTemplate.is_html
      }
    });
  };

  const handleDeleteTemplate = async (templateId, templateName) => {
    console.log("Delete button clicked for template:", templateId, templateName);
    
    if (!templateId) {
      console.error("Error: No template ID provided");
      return;
    }

    // Set the delete confirmation state to show the dialog
    setDeleteConfirm({ id: templateId, name: templateName });
  };

  const confirmDeleteTemplate = async () => {
    if (!deleteConfirm) return;
    
    console.log("Making delete API call for:", deleteConfirm.id);
    deleteTemplateMutation.mutate(deleteConfirm.id);
    setDeleteConfirm(null);
  };

  const handlePreviewTemplate = async (template) => {
    setSelectedTemplate(template);
    
    // Initialize preview variables if template has variables
    if (template.variables && template.variables.length > 0) {
      const initialVars = {};
      template.variables.forEach(varName => {
        // Set sample values for preview
        const variable = getAvailableVariables(template.category).find(v => v.name === varName);
        if (variable) {
          switch (varName) {
            // Customer/Lead/Project common fields
            case "name": initialVars[varName] = "John Smith"; break;
            case "email": initialVars[varName] = "john@example.com"; break;
            case "phone": initialVars[varName] = "(555) 123-4567"; break;
            case "address": initialVars[varName] = "123 Main St, City, State"; break;
            case "city": initialVars[varName] = "Sacramento"; break;
            case "zip_code": initialVars[varName] = "95814"; break;
            
            // Project specific
            case "jobsite_address": initialVars[varName] = "456 Oak Ave, City, State"; break;
            case "contract_price": initialVars[varName] = "$15,000"; break;
            case "total_job_cost": initialVars[varName] = "$16,500"; break;
            case "status": initialVars[varName] = "In Progress"; break;
            case "project_type": initialVars[varName] = "Construction"; break;
            
            // Lead specific
            case "service_type": initialVars[varName] = "Landscape Design"; break;
            case "referral_source": initialVars[varName] = "Google"; break;
            case "priority": initialVars[varName] = "High"; break;
            case "project_value": initialVars[varName] = "$25,000"; break;
            
            // Company variables
            case "company_name": initialVars[varName] = "Reliable Landscaping Design"; break;
            case "company_phone": initialVars[varName] = "(916) 918-3990"; break;
            case "company_email": initialVars[varName] = "info@reliablelandscapesdesign.com"; break;
            case "date": initialVars[varName] = new Date().toLocaleDateString(); break;
            case "time": initialVars[varName] = new Date().toLocaleTimeString(); break;
            case "year": initialVars[varName] = new Date().getFullYear().toString(); break;
            
            default: initialVars[varName] = `[${varName}]`;
          }
        } else {
          initialVars[varName] = `[${varName}]`;
        }
      });
      setPreviewVariables(initialVars);
    }
    
    setIsPreviewDialogOpen(true);
  };

  const handleGeneratePreview = async () => {
    previewTemplateMutation.mutate({
      id: selectedTemplate.id || selectedTemplate._id,
      variables: previewVariables
    });
  };

  const insertVariable = (variableName, isSubject = false) => {
    const placeholder = `{${variableName}}`;
    if (isSubject) {
      setNewTemplate(prev => ({
        ...prev,
        subject: prev.subject + placeholder
      }));
    } else {
      setNewTemplate(prev => ({
        ...prev,
        body: prev.body + placeholder
      }));
    }
  };

  const insertVariableEdit = (variableName, isSubject = false) => {
    const placeholder = `{${variableName}}`;
    if (isSubject) {
      setEditingTemplate(prev => ({
        ...prev,
        subject: prev.subject + placeholder
      }));
    } else {
      setEditingTemplate(prev => ({
        ...prev,
        body: prev.body + placeholder
      }));
    }
  };

  if (loading) return <LoadingCard message="Loading email templates..." />;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Email Templates</h1>
          <p className="text-gray-600 mt-1">Create and manage reusable email templates with dynamic content</p>
        </div>
        <div className="flex space-x-3">
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-brand-600 hover:bg-brand-700">
                <Plus className="mr-2 h-4 w-4" />
                New Template
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Create Email Template</DialogTitle>
                <DialogDescription>
                  Create a reusable email template with dynamic variables like {"{name}"} or {"{referral_source}"}.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="templateName">Template Name *</Label>
                    <Input
                      id="templateName"
                      value={newTemplate.name}
                      onChange={(e) => setNewTemplate({...newTemplate, name: e.target.value})}
                      placeholder="Welcome Email, Project Update, etc."
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="templateCategory">Category</Label>
                    <Select value={newTemplate.category} onValueChange={(value) => setNewTemplate({...newTemplate, category: value})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {EMAIL_CATEGORIES.map((category) => (
                          <SelectItem key={category.value} value={category.value}>
                            {category.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* HTML Mode Toggle */}
                <div className="flex items-center space-x-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <Code className="h-5 w-5 text-blue-600" />
                  <div className="flex-1">
                    <Label htmlFor="html-mode" className="text-sm font-medium text-blue-900">
                      HTML Mode
                    </Label>
                    <p className="text-xs text-blue-700">
                      Enable to use HTML formatting in your email body
                    </p>
                  </div>
                  <Switch
                    id="html-mode"
                    checked={newTemplate.is_html}
                    onCheckedChange={(checked) => setNewTemplate({...newTemplate, is_html: checked})}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="templateSubject">Email Subject *</Label>
                  <Input
                    id="templateSubject"
                    value={newTemplate.subject}
                    onChange={(e) => setNewTemplate({...newTemplate, subject: e.target.value})}
                    placeholder="Welcome {name}! Let's discuss your project"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="templateBody">
                    Email Body * {newTemplate.is_html && <Badge variant="secondary" className="ml-2">HTML</Badge>}
                  </Label>
                  <Textarea
                    id="templateBody"
                    value={newTemplate.body}
                    onChange={(e) => setNewTemplate({...newTemplate, body: e.target.value})}
                    placeholder={newTemplate.is_html ? 
                      '<html><body><h1>Hello {name}</h1><p>Welcome to our service...</p></body></html>' :
                      'Dear {name}, Thank you for choosing our services. We received your inquiry through {referral_source}...'
                    }
                    rows={8}
                    className="font-mono text-sm"
                  />
                  {newTemplate.is_html && (
                    <p className="text-xs text-gray-500">
                      💡 Tip: You can use full HTML including tables, styling, images, etc.
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="templateDescription">Description</Label>
                  <Input
                    id="templateDescription"
                    value={newTemplate.description}
                    onChange={(e) => setNewTemplate({...newTemplate, description: e.target.value})}
                    placeholder="Brief description of when to use this template"
                  />
                </div>

                {/* Variable Helper */}
                <div className="border rounded-lg p-4 bg-gray-50">
                  <Label className="text-sm font-medium">Available Variables for {EMAIL_CATEGORIES.find(cat => cat.value === newTemplate.category)?.label} Templates</Label>
                  <div className="text-xs text-gray-600 mb-2">Click any variable to insert it into your template</div>
                  <div className="grid grid-cols-2 gap-1 mt-2 max-h-40 overflow-y-auto">
                    {getAvailableVariables(newTemplate.category).map((variable) => (
                      <Button
                        key={variable.name}
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-8 px-2 justify-start text-xs font-mono"
                        title={variable.description}
                        onClick={() => {
                          const textarea = document.getElementById('templateBody');
                          if (textarea) {
                            const cursorPos = textarea.selectionStart;
                            const textBefore = newTemplate.body.substring(0, cursorPos);
                            const textAfter = newTemplate.body.substring(cursorPos);
                            const newText = textBefore + `{${variable.name}}` + textAfter;
                            setNewTemplate({...newTemplate, body: newText});
                            // Re-focus and set cursor position
                            setTimeout(() => {
                              textarea.focus();
                              textarea.setSelectionRange(cursorPos + variable.name.length + 2, cursorPos + variable.name.length + 2);
                            }, 0);
                          }
                        }}
                      >
                        {`{${variable.name}}`}
                      </Button>
                    ))}
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleAddTemplate}
                  disabled={createTemplateMutation.isPending || !newTemplate.name || !newTemplate.subject || !newTemplate.body}
                  className="bg-brand-600 hover:bg-brand-700"
                >
                  {createTemplateMutation.isPending ? "Creating..." : "Create Template"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center space-x-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search templates..."
                  className="pl-8"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-48">
                <Filter className="mr-2 h-4 w-4" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {EMAIL_CATEGORIES.map((category) => (
                  <SelectItem key={category.value} value={category.value}>
                    {category.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Templates Table */}
      <Card>
        <CardHeader>
          <CardTitle>Email Templates</CardTitle>
          <CardDescription>
            Manage your email templates and customize them with dynamic variables
          </CardDescription>
        </CardHeader>
        <CardContent>
          {templates.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Subject</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Variables</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {templates.map((template) => (
                  <TableRow key={template.id || template._id}>
                    <TableCell>
                      <div>
                        <div className="font-medium">{template.name}</div>
                        {template.description && (
                          <div className="text-sm text-gray-500">{template.description}</div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="max-w-xs">
                      <div className="truncate" title={template.subject}>
                        {template.subject}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Badge variant="outline" className="capitalize">
                          {template.category}
                        </Badge>
                        {template.is_html && (
                          <Badge variant="secondary" className="bg-blue-100 text-blue-800">
                            <Code className="h-3 w-3 mr-1" />
                            HTML
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {template.variables && template.variables.slice(0, 3).map((variable, index) => (
                          <Badge key={index} variant="secondary" className="text-xs">
                            {"{" + variable + "}"}
                          </Badge>
                        ))}
                        {template.variables && template.variables.length > 3 && (
                          <Badge variant="secondary" className="text-xs">
                            +{template.variables.length - 3}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {new Date(template.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end space-x-2">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handlePreviewTemplate(template)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleEditTemplate(template)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleDeleteTemplate(template.id || template._id, template.name)}
                          disabled={deleteTemplateMutation.isPending}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-12">
              <Mail className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">No email templates found</p>
              <p className="text-gray-400 mb-6">Create your first template to get started</p>
              <Button 
                onClick={() => setIsDialogOpen(true)}
                className="bg-brand-600 hover:bg-brand-700"
              >
                <Plus className="mr-2 h-4 w-4" />
                Create First Template
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Template Dialog - Similar to Create but with editing functionality */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Email Template</DialogTitle>
            <DialogDescription>
              Update your email template and modify variables as needed.
            </DialogDescription>
          </DialogHeader>
          {editingTemplate && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="editTemplateName">Template Name *</Label>
                  <Input
                    id="editTemplateName"
                    value={editingTemplate.name}
                    onChange={(e) => setEditingTemplate({...editingTemplate, name: e.target.value})}
                    placeholder="Template name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="editTemplateCategory">Category</Label>
                  <Select value={editingTemplate.category} onValueChange={(value) => setEditingTemplate({...editingTemplate, category: value})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {EMAIL_CATEGORIES.map((category) => (
                        <SelectItem key={category.value} value={category.value}>
                          {category.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* HTML Mode Toggle for Edit */}
              <div className="flex items-center space-x-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <Code className="h-5 w-5 text-blue-600" />
                <div className="flex-1">
                  <Label htmlFor="edit-html-mode" className="text-sm font-medium text-blue-900">
                    HTML Mode
                  </Label>
                  <p className="text-xs text-blue-700">
                    Enable to use HTML formatting in your email body
                  </p>
                </div>
                <Switch
                  id="edit-html-mode"
                  checked={editingTemplate.is_html || false}
                  onCheckedChange={(checked) => setEditingTemplate({...editingTemplate, is_html: checked})}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="editTemplateSubject">Email Subject *</Label>
                <Input
                  id="editTemplateSubject"
                  value={editingTemplate.subject}
                  onChange={(e) => setEditingTemplate({...editingTemplate, subject: e.target.value})}
                  placeholder="Email subject"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="editTemplateBody">
                  Email Body * {editingTemplate.is_html && <Badge variant="secondary" className="ml-2">HTML</Badge>}
                </Label>
                <Textarea
                  id="editTemplateBody"
                  value={editingTemplate.body}
                  onChange={(e) => setEditingTemplate({...editingTemplate, body: e.target.value})}
                  placeholder={editingTemplate.is_html ? 
                    '<html><body><h1>Hello {name}</h1><p>Welcome to our service...</p></body></html>' :
                    'Email body content'
                  }
                  rows={8}
                  className="font-mono text-sm"
                />
                {editingTemplate.is_html && (
                  <p className="text-xs text-gray-500">
                    💡 Tip: You can use full HTML including tables, styling, images, etc.
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="editTemplateDescription">Description</Label>
                <Input
                  id="editTemplateDescription"
                  value={editingTemplate.description || ""}
                  onChange={(e) => setEditingTemplate({...editingTemplate, description: e.target.value})}
                  placeholder="Template description"
                />
              </div>

              {/* Variable Helper for Edit */}
              <div className="border rounded-lg p-4 bg-gray-50">
                <Label className="text-sm font-medium">Available Variables for {EMAIL_CATEGORIES.find(cat => cat.value === editingTemplate.category)?.label} Templates</Label>
                <div className="text-xs text-gray-600 mb-2">Click any variable to insert it into your template</div>
                <div className="grid grid-cols-2 gap-1 mt-2 max-h-40 overflow-y-auto">
                  {getAvailableVariables(editingTemplate.category).map((variable) => (
                    <Button
                      key={variable.name}
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-8 px-2 justify-start text-xs font-mono"
                      title={variable.description}
                      onClick={() => {
                        const textarea = document.getElementById('editTemplateBody');
                        if (textarea) {
                          const cursorPos = textarea.selectionStart;
                          const textBefore = editingTemplate.body.substring(0, cursorPos);
                          const textAfter = editingTemplate.body.substring(cursorPos);
                          const newText = textBefore + `{${variable.name}}` + textAfter;
                          setEditingTemplate({...editingTemplate, body: newText});
                          // Re-focus and set cursor position
                          setTimeout(() => {
                            textarea.focus();
                            textarea.setSelectionRange(cursorPos + variable.name.length + 2, cursorPos + variable.name.length + 2);
                          }, 0);
                        }
                      }}
                    >
                      {`{${variable.name}}`}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleUpdateTemplate}
              disabled={updateTemplateMutation.isPending || !editingTemplate?.name || !editingTemplate?.subject || !editingTemplate?.body}
              className="bg-brand-600 hover:bg-brand-700"
            >
              {updateTemplateMutation.isPending ? "Updating..." : "Update Template"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Template Dialog */}
      <Dialog open={isPreviewDialogOpen} onOpenChange={setIsPreviewDialogOpen}>
        <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Preview Email Template</DialogTitle>
            <DialogDescription>
              Preview how your email will look with actual data. Adjust the variable values below.
            </DialogDescription>
          </DialogHeader>
          {selectedTemplate && (
            <div className="space-y-4 py-4">
              {/* Variable inputs if template has variables */}
              {selectedTemplate.variables && selectedTemplate.variables.length > 0 && (
                <div className="space-y-4">
                  <Label className="text-sm font-medium">Variable Values</Label>
                  <div className="grid grid-cols-2 gap-4 max-h-48 overflow-y-auto">
                    {selectedTemplate.variables.map((variable) => (
                      <div key={variable} className="space-y-2">
                        <Label htmlFor={`var-${variable}`} className="text-xs">
                          {"{" + variable + "}"}
                        </Label>
                        <Input
                          id={`var-${variable}`}
                          size="sm"
                          value={previewVariables[variable] || ""}
                          onChange={(e) => setPreviewVariables({
                            ...previewVariables,
                            [variable]: e.target.value
                          })}
                          placeholder={`Enter ${variable}`}
                        />
                      </div>
                    ))}
                  </div>
                  <Button 
                    onClick={handleGeneratePreview}
                    disabled={previewTemplateMutation.isPending}
                    className="bg-brand-600 hover:bg-brand-700"
                  >
                    {previewTemplateMutation.isPending ? "Generating..." : "Generate Preview"}
                  </Button>
                </div>
              )}

              {/* Preview Display */}
              {previewData ? (
                <div className="space-y-4 border rounded-lg p-4">
                  <div>
                    <Label className="text-sm font-medium">Subject Preview</Label>
                    <div className="mt-1 p-2 bg-gray-50 rounded border">
                      {previewData.preview_subject}
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Body Preview</Label>
                    <div className="mt-1 p-4 bg-white border rounded max-h-96 overflow-y-auto">
                      {/* Auto-detect HTML content or use is_html flag */}
                      {(selectedTemplate.is_html || /<[a-z][\s\S]*>/i.test(previewData.preview_body)) ? (
                        <div dangerouslySetInnerHTML={{ __html: previewData.preview_body }} />
                      ) : (
                        <div className="whitespace-pre-wrap">{previewData.preview_body}</div>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  {selectedTemplate.variables && selectedTemplate.variables.length > 0 
                    ? "Adjust variable values and click 'Generate Preview' to see the email"
                    : "This template has no variables. The email will display as written."
                  }
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setIsPreviewDialogOpen(false);
              setPreviewData(null);
            }}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Email Template</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{deleteConfirm?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDeleteTemplate} disabled={deleteTemplateMutation.isPending}>
              {deleteTemplateMutation.isPending ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}